// A small, safe markdown renderer for agent output (no HTML injection: everything
// becomes React elements). Supports headings, **bold**, *italic*, `code`,
// bullet/numbered lists, rules and paragraphs — the subset models actually write.
import type { ReactNode } from "react";

function inline(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*\n]+\*\*|`[^`\n]+`|\*[^*\s][^*\n]*\*|_[^_\s][^_\n]*_)/g;
  let last = 0, i = 0, m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const t = m[0];
    const k = `${keyBase}-${i++}`;
    if (t.startsWith("**")) out.push(<strong key={k}>{t.slice(2, -2)}</strong>);
    else if (t.startsWith("`")) out.push(<code key={k} className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em]">{t.slice(1, -1)}</code>);
    else out.push(<em key={k}>{t.slice(1, -1)}</em>);
    last = m.index + t.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function Markdown({ text, className = "" }: { text: string; className?: string }) {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  let para: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  let n = 0;

  const flushPara = () => {
    if (para.length) { const k = `p${n++}`; blocks.push(<p key={k} className="whitespace-pre-wrap">{inline(para.join("\n"), k)}</p>); para = []; }
  };
  const flushList = () => {
    if (!list) return;
    const k = `l${n++}`;
    const items = list.items.map((it, j) => <li key={j}>{inline(it, `${k}-${j}`)}</li>);
    blocks.push(list.ordered
      ? <ol key={k} className="list-decimal space-y-0.5 pl-5">{items}</ol>
      : <ul key={k} className="list-disc space-y-0.5 pl-5">{items}</ul>);
    list = null;
  };

  for (const line of lines) {
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    const ul = line.match(/^\s*[-*•]\s+(.*)$/);
    const ol = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (h) {
      flushPara(); flushList();
      const k = `h${n++}`;
      blocks.push(<p key={k} className={`font-semibold ${h[1].length <= 2 ? "text-base" : "text-sm"}`}>{inline(h[2], k)}</p>);
    } else if (/^\s*([-*_])\1{2,}\s*$/.test(line)) {
      flushPara(); flushList(); blocks.push(<hr key={`r${n++}`} className="border-border" />);
    } else if (ul || ol) {
      flushPara();
      const ordered = !!ol && !ul;
      if (list && list.ordered !== ordered) flushList();
      list ??= { ordered, items: [] };
      list.items.push((ul ?? ol)![1]);
    } else if (line.trim() === "") {
      flushPara(); flushList();
    } else {
      flushList(); para.push(line);
    }
  }
  flushPara(); flushList();
  return <div className={`space-y-2 text-sm ${className}`}>{blocks}</div>;
}
